import * as React from "react";
import { io } from "socket.io-client";
import appReducer from "../reducers/AppReducer.js";
import { AppContext } from "../contexts/AppContext.js";
import { REACT_APP_NAME, REACT_APP_API_BASE_URL } from "../../config";
import SOCKET_EVENTS from "../../config/SocketEvents";

const createSocket = (accessToken) =>
  io(REACT_APP_API_BASE_URL, {
    autoConnect: Boolean(accessToken),
    transportOptions: {
      polling: {
        extraHeaders: {
          "x-access-token": accessToken || "",
        },
      },
      websocket: {
        extraHeaders: {
          "x-access-token": accessToken || "",
        },
      },
    },
  });

const createInitialState = () => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  return {
    appName: REACT_APP_NAME,
    accessToken,
    refreshToken,
    userData: {},
    chats: [],
    chatsPaginations: {},
    allGroupChats: [],
    mutedActiveRoomParticipantIds: {},
    dataLoading: false,
    groupChatLoading: true,
    chatLoading: true,
    filteredChats: { success: false, direct_chats: {}, group_chats: {}, new_users: [] },
    waitingRoomParticipantRequests: (() => {
      try {
        const stored = localStorage.getItem("waitingRoomParticipantRequests");
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    })(),
    searchInProgress: false,
    activeChat: {},
    roomToolbox: {
      isOpen: false,
      currentUserToolboxEnabled: false,
    },
    socket: createSocket(accessToken),
  };
};

function AppProvider({ children }) {
  const [state, dispatch] = React.useReducer(
    appReducer,
    undefined,
    createInitialState,
  );

  // Shared popup notification state — must live here so all useApp() callers read the same array
  const [popupNotifications, setPopupNotifications] = React.useState([]);

  React.useEffect(() => {
    const token = state.accessToken || localStorage.getItem("accessToken") || "";

    state.socket.auth = {
      ...(state.socket.auth || {}),
      token,
    };

    state.socket.io.opts.transportOptions = {
      ...(state.socket.io.opts.transportOptions || {}),
      polling: {
        extraHeaders: {
          "x-access-token": token,
        },
      },
      websocket: {
        extraHeaders: {
          "x-access-token": token,
        },
      },
    };

    if (token && !state.socket.connected) {
      state.socket.connect();
    }

    if (!token && state.socket.connected) {
      state.socket.disconnect();
    }
  }, [state.accessToken, state.socket]);

  React.useEffect(() => {
    const handleDisconnect = () => {
      console.log("Socket disconnected");
    };

    // On every successful reconnect re-announce the user as online.
    // socket.io fires 'connect' on both the initial connection and every reconnect;
    // we use the manager-level 'reconnect' event to target reconnects only.
    const handleReconnect = () => {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) state.socket.emit(SOCKET_EVENTS.LOGIN_USER);
    };

    state.socket.on(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
    state.socket.io.on("reconnect", handleReconnect);

    return () => {
      state.socket.off(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
      state.socket.io.off("reconnect", handleReconnect);
      state.socket.disconnect();
    };
  }, [state.socket]);

  const value = [state, dispatch, popupNotifications, setPopupNotifications];

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export default AppProvider;
